import { dateLib, moneyLib, roomLib } from "@agentcall/core/lib"
import type { PlanRes, RoomRes, TenantRes } from "@agentcall/core/res"
import { fileType, planMasterType, tenantType } from "@agentcall/core/type"
import { type ReactNode, useState } from "react"
import { Camera, CreditCard, Edit, Edit2, FileText, Plus, Users } from "react-feather"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent, EmptyCard } from "@/components/ui/card"
import { cn } from "@/lib/utils"

// Types

interface AttachmentItem {
  id: string
  title: string
  uploadDate: string
  imageUrl?: string
  fileName?: string | null
  fileType?: string | null
  isEmpty?: boolean
}

const IMAGE_FILE_EXTENSION_REGEX = /\.(jpe?g|png|gif|webp|bmp|svg)$/i
const isImageFile = (fileType: string | null | undefined, fileName?: string | null) =>
  fileType?.startsWith("image/") || (!!fileName && IMAGE_FILE_EXTENSION_REGEX.test(fileName))

// Section Components
interface SectionHeaderProps {
  icon: ReactNode
  title: string
  action?: ReactNode
}

function SectionHeader({ icon, title, action }: SectionHeaderProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-base font-semibold text-[#354267]">{title}</h2>
      </div>
      {action}
    </div>
  )
}

// Fee Information Card
interface FeeInfoCardProps {
  tenant?: TenantRes | null
  onEdit?: () => void
}

interface FeeItem {
  label: string
  amount: number | null
  detail: string
}

export function FeeInfoCard({ tenant, onEdit }: FeeInfoCardProps) {
  const otherFees = tenant?.otherFees ?? []

  // 値が設定されているかどうか（編集ボタン表示の判定用）
  const hasValues =
    tenant?.rent !== undefined ||
    tenant?.commonAreaFee !== undefined ||
    tenant?.parkingFee !== undefined ||
    otherFees.length > 0

  const isBusiness = tenant?.purpose === tenantType.TENANT_PURPOSE_BUSINESS

  const feeItems: FeeItem[] = [
    {
      label: "家賃",
      amount: isBusiness
        ? moneyLib.calculateTaxIncludedOrNull(tenant?.rent)
        : (tenant?.rent ?? null),
      detail: isBusiness
        ? moneyLib.formatPriceDetail(tenant?.rent, moneyLib.calculateTaxOrNull(tenant?.rent))
        : "",
    },
    {
      label: "共益費・管理費",
      amount: isBusiness
        ? moneyLib.calculateTaxIncludedOrNull(tenant?.commonAreaFee)
        : (tenant?.commonAreaFee ?? null),
      detail: isBusiness
        ? moneyLib.formatPriceDetail(
            tenant?.commonAreaFee,
            moneyLib.calculateTaxOrNull(tenant?.commonAreaFee)
          )
        : "",
    },
    {
      label: "駐車場",
      amount: moneyLib.calculateTaxIncludedOrNull(tenant?.parkingFee),
      detail: moneyLib.formatPriceDetail(
        tenant?.parkingFee,
        moneyLib.calculateTaxOrNull(tenant?.parkingFee)
      ),
    },
    ...otherFees.map((fee) => ({
      label: fee.label,
      amount: fee.price + fee.tax,
      detail: moneyLib.formatPriceDetail(fee.price, fee.tax),
    })),
  ]

  const totalAmount = feeItems.reduce((sum, item) => {
    return sum + (item.amount ?? 0)
  }, 0)

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col gap-[15px]">
          <SectionHeader
            icon={<CreditCard className="h-4 w-4 text-[#9EAFC7]" />}
            title="料金情報"
            action={
              hasValues &&
              onEdit && (
                <Button variant="outline" size="sm" className="gap-[5px]" onClick={onEdit}>
                  <Edit2 className="h-3.5 w-3.5" />
                  編集
                </Button>
              )
            }
          />

          {feeItems.map((item) => (
            <div key={item.label} className="flex gap-[10px] items-start">
              <div className="flex-1">
                <p className="text-sm font-medium leading-5 text-[#3F4652] tracking-[-0.168px]">
                  {item.label}
                </p>
              </div>
              <div className="flex-1 flex flex-col gap-[3px] text-right">
                <p className="text-lg font-semibold leading-5 text-[#3F4652] tracking-[-0.216px]">
                  {moneyLib.formatAmount(item.amount)}
                </p>
                {item.detail && (
                  <p className="text-xs font-semibold leading-[14px] text-[#9EAFC7] tracking-[-0.144px]">
                    {item.detail}
                  </p>
                )}
              </div>
            </div>
          ))}

          <div className="border-t border-slate-200" />

          <div className="flex gap-[10px] items-start">
            <div className="flex-1">
              <p className="text-sm font-medium leading-5 text-[#3F4652] tracking-[-0.168px]">
                月額合計
              </p>
            </div>
            <div className="flex-1 text-right">
              <p className="text-lg font-semibold leading-5 text-[#3F4652] tracking-[-0.216px]">
                {moneyLib.formatAmount(totalAmount > 0 ? totalAmount : null)}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Attachment Card
interface AttachmentCardProps {
  attachment: AttachmentItem
  /** 添付ファイルのインデックス（0: 賃貸借契約書, 1: 重要事項説明書） */
  index: number
  onViewDetail?: () => void
}

// 添付ファイルの固定タイトル（1,2枚目のみ固定）
const ATTACHMENT_FIXED_TITLES: Record<number, string> = {
  0: "賃貸借契約書",
  1: "重要事項説明書",
}

export function AttachmentCard({ attachment, index, onViewDetail }: AttachmentCardProps) {
  // 1,2枚目は固定タイトル、3枚目以降は自由タイトル
  const displayTitle = ATTACHMENT_FIXED_TITLES[index] ?? attachment.title
  const isImage = isImageFile(attachment.fileType, attachment.fileName)

  return (
    <Card>
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-base font-semibold text-slate-600">{displayTitle}</h3>
            {!attachment.isEmpty && (
              <p className="text-xs text-slate-400">アップロード日: {attachment.uploadDate}</p>
            )}
          </div>

          {attachment.isEmpty ? (
            <div className="flex h-[92px] w-full items-center justify-center rounded-md bg-slate-100 px-4 text-sm font-semibold text-slate-400">
              情報がありません
            </div>
          ) : (
            attachment.imageUrl &&
            (isImage ? (
              <div className="h-[165px] w-full rounded-md bg-slate-100">
                <img
                  src={attachment.imageUrl}
                  alt={displayTitle}
                  className="h-full w-full rounded-md object-cover"
                />
              </div>
            ) : (
              <a
                href={attachment.imageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-[92px] w-full items-center gap-3 rounded-md bg-slate-100 px-4 text-slate-600"
              >
                <FileText className="size-8 shrink-0 text-slate-400" />
                <span className="min-w-0 truncate text-sm font-semibold">
                  {attachment.fileName || displayTitle || "添付ファイル"}
                </span>
              </a>
            ))
          )}

          <Button variant="outline" className="w-full gap-2" onClick={onViewDetail}>
            <Edit className="h-3.5 w-3.5" />
            詳細を見る
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// Attachments Section
const ATTACHMENTS_PER_PAGE = 3
const MAX_ATTACHMENTS = 6

interface AttachmentsSectionProps {
  attachments: AttachmentItem[]
  onAdd?: () => void
  onViewDetail?: (id: string) => void
}

export function AttachmentsSection({ attachments, onAdd, onViewDetail }: AttachmentsSectionProps) {
  const [displayCount, setDisplayCount] = useState(ATTACHMENTS_PER_PAGE)

  const displayedAttachments = attachments.slice(0, displayCount)
  const hasMore = displayCount < attachments.length
  const realAttachmentCount = attachments.filter((attachment) => !attachment.isEmpty).length
  const isMaxReached = realAttachmentCount >= MAX_ATTACHMENTS

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + ATTACHMENTS_PER_PAGE)
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        icon={<Camera className="h-4 w-4 text-slate-400" />}
        title="添付ファイル"
        action={
          onAdd && (
            <Button onClick={onAdd} disabled={isMaxReached}>
              <Plus className="h-3.5 w-3.5" />
              追加
            </Button>
          )
        }
      />

      <div className="space-y-3">
        {displayedAttachments.map((attachment, index) => (
          <AttachmentCard
            key={attachment.id}
            attachment={attachment}
            index={index}
            onViewDetail={() => onViewDetail?.(attachment.id)}
          />
        ))}
        {hasMore && (
          <Button variant="secondary" block onClick={handleLoadMore}>
            もっと見る
          </Button>
        )}
      </div>
    </div>
  )
}

// Past Tenant Card
interface PastTenantCardProps {
  tenant: TenantRes
}

export function PastTenantCard({ tenant }: PastTenantCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div className="space-y-1">
              <p className="text-xs text-slate-400">氏名</p>
              <p className="text-sm font-medium text-slate-600">{tenant.name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-400">氏名（カナ）</p>
              <p className="text-sm font-medium text-slate-600">{tenant.nameKana}</p>
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-slate-400">初回賃貸借契約開始日</p>
            <p className="text-sm font-medium text-slate-600">{tenant.startDate ?? "―"}</p>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-slate-400">賃貸借契約終了日</p>
            <p className="text-sm font-medium text-slate-600">{tenant.endDate ?? "―"}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Past Tenants Section
const TENANTS_PER_PAGE = 3

interface PastTenantsSectionProps {
  tenants: TenantRes[]
}

export function PastTenantsSection({ tenants }: PastTenantsSectionProps) {
  const [displayCount, setDisplayCount] = useState(TENANTS_PER_PAGE)

  const hasTenants = tenants.length > 0
  const displayedTenants = tenants.slice(0, displayCount)
  const hasMore = displayCount < tenants.length

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + TENANTS_PER_PAGE)
  }

  return (
    <div className="space-y-4">
      <SectionHeader icon={<Users className="h-4 w-4 text-slate-400" />} title="過去の賃借人" />

      <div className="space-y-3">
        {hasTenants ? (
          <>
            {displayedTenants.map((tenant) => (
              <PastTenantCard key={tenant.id} tenant={tenant} />
            ))}
            {hasMore && (
              <Button variant="secondary" block onClick={handleLoadMore}>
                もっと見る
              </Button>
            )}
          </>
        ) : (
          <EmptyCard text="情報がありません" />
        )}
      </div>
    </div>
  )
}

interface PastPlansSectionProps {
  plans: PlanRes[]
}

export function PastPlansSection({ plans }: PastPlansSectionProps) {
  const formatPeriod = (start?: string | null, end?: string | null) => {
    if (!start && !end) return "―"
    if (start && end) {
      const endDateOfMonth = dateLib.getMonthEnd(dateLib.getYearMonth(end))
      return `${start} 〜 ${endDateOfMonth}`
    }
    if (start) return `${start} 〜`
    if (end) {
      const endDateOfMonth = dateLib.getMonthEnd(dateLib.getYearMonth(end))
      return endDateOfMonth
    }
    return "―"
  }

  const formatMonthlyPrice = (plan: PlanRes) => {
    // エコーズプランは Echoes 側で請求するため AgentCall の月額(0 円)は出さない。
    if (planMasterType.isEchoesPlanMasterId(plan.planMasterId)) return "Echoes請求"
    const formatted = moneyLib.formatPrice(plan.monthlyPrice)
    if (formatted === "未設定") return formatted
    return `${formatted}/月`
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        icon={<CreditCard className="h-4 w-4 text-slate-400" />}
        title="過去のプラン"
      />

      {plans.length === 0 ? (
        <EmptyCard text="過去のプランはありません" />
      ) : (
        <div className="space-y-3">
          {plans.map((plan) => (
            <Card key={plan.id}>
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between items-start gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-slate-700">
                      {plan.planMaster?.name ?? "プラン名未設定"}
                    </p>
                    <p className="text-xs text-slate-400">
                      契約期間: {formatPeriod(plan.startDate, plan.endDate)}
                    </p>
                    {plan.echoesPlanSuspended && (
                      <Badge variant="warning" size="sm">
                        停止中に終了
                      </Badge>
                    )}
                  </div>
                  <div className="shrink-0 whitespace-nowrap text-sm font-semibold text-slate-600">
                    {formatMonthlyPrice(plan)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

interface RoomRelatedInfoProps {
  room: RoomRes
  onAddTenant: () => void
  onEditFee?: () => void
  onAddAttachment?: () => void
  onViewAttachmentDetail?: (id: string) => void
  className?: string
}

export function RoomRelatedInfo({
  room,
  onEditFee,
  onAddAttachment,
  onViewAttachmentDetail,
  className,
}: RoomRelatedInfoProps) {
  const currentTenant = roomLib.roomCurrentTenant(room)
  const pastTenants = roomLib.roomPastTenants(room)
  const pastPlans = roomLib.roomPastPlans(room)

  const tenantFiles = currentTenant?.files ?? []
  const rentalContractFile = tenantFiles.find(
    (file) => file.tenantFileType === fileType.TENANT_FILE_TYPE_RENTAL_CONTRACT
  )
  const importantMattersFile = tenantFiles.find(
    (file) => file.tenantFileType === fileType.TENANT_FILE_TYPE_IMPORTANT_MATTERS
  )
  const otherFiles = tenantFiles.filter(
    (file) =>
      file.tenantFileType !== fileType.TENANT_FILE_TYPE_RENTAL_CONTRACT &&
      file.tenantFileType !== fileType.TENANT_FILE_TYPE_IMPORTANT_MATTERS
  )

  const toAttachment = (
    file: NonNullable<TenantRes["files"]>[number] | undefined,
    title: string,
    emptyId: string
  ): AttachmentItem => ({
    id: file ? String(file.id) : emptyId,
    title: file?.title ?? file?.fileName ?? title,
    uploadDate: file?.createdAt ? dateLib.formatDateTime(file.createdAt) : "",
    imageUrl: file?.url,
    fileName: file?.fileName,
    fileType: file?.fileType,
    isEmpty: !file,
  })

  // 現在の賃借人のfilesからattachmentsを生成
  const attachments: AttachmentItem[] = [
    toAttachment(
      rentalContractFile,
      fileType.TENANT_FILE_TITLE_RENTAL_CONTRACT,
      "empty-rental-contract"
    ),
    toAttachment(
      importantMattersFile,
      fileType.TENANT_FILE_TITLE_IMPORTANT_MATTERS,
      "empty-important-matters"
    ),
    ...otherFiles.map((file) => ({
      id: String(file.id),
      title: file.title ?? file.fileName ?? "添付ファイル",
      uploadDate: file.createdAt ? dateLib.formatDateTime(file.createdAt) : "",
      imageUrl: file.url,
      fileName: file.fileName,
      fileType: file.fileType,
    })),
  ]

  return (
    <div className={cn("space-y-8", className)}>
      <FeeInfoCard tenant={currentTenant} onEdit={onEditFee} />
      <hr className="border-slate-200" />

      <AttachmentsSection
        attachments={attachments}
        onAdd={onAddAttachment}
        onViewDetail={onViewAttachmentDetail}
      />

      <hr className="border-slate-200" />

      <PastTenantsSection tenants={pastTenants} />

      <hr className="border-slate-200" />

      <PastPlansSection plans={pastPlans} />
    </div>
  )
}
